import React, { useEffect } from 'react';
import styled from 'styled-components';
import { logger } from '../../utils/logger';
import { formatDateToLong } from '../../utils/dateUtils';

// Styled Components
const FormSection = styled.section`
  margin-bottom: 2rem;
  
  h3 {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f8f9fa;
    color: var(--primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--dark);
  }
  
  .form-text {
    font-size: 0.85rem;
    color: var(--gray);
    margin-top: 0.25rem;
  }
  
  input, select, textarea {
    border: 2px solid ${props => props.hasError ? 'var(--primary)' : '#f0f0f0'};
    border-radius: 8px;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: ${props => props.hasError ? 'var(--primary)' : 'var(--primary-light)'};
      box-shadow: 0 0 0 0.25rem ${props => props.hasError ? 'rgba(233, 64, 87, 0.25)' : 'rgba(233, 64, 87, 0.25)'};
    }
  }
  
  .error-message {
    color: var(--primary);
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  background-color: ${props => props.checked ? 'rgba(233, 64, 87, 0.1)' : '#f8f9fa'};
  border: 1px solid ${props => props.checked ? 'var(--primary)' : '#e9ecef'};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.checked ? 'rgba(233, 64, 87, 0.15)' : '#e9ecef'};
  }
  
  input {
    margin-right: 0.5rem;
  }
`;

const ReadOnlyField = styled.div`
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const InfoText = styled.div`
  font-size: 0.85rem;
  color: var(--gray);
  margin-top: 0.25rem;
  margin-bottom: 1rem;
  font-style: italic;
`;

const BasicInfoSection = ({ formData, handleChange, errors, isEditing = false }) => {
  useEffect(() => {
    logger.debug('BasicInfoSection received form data:', {
      bedtime: formData.bedtime,
      wakeupTime: formData.wakeupTime,
      numberOfNaps: formData.numberOfNaps,
      napDurations: formData.napDurations
    });
  }, [formData]);

  return (
    <FormSection>
      {isEditing ? (
        <>
          <FormGroup>
            <label>Baby's Name</label>
            <ReadOnlyField>{formData.babyName || 'Not provided'}</ReadOnlyField>
            <InfoText>Baby's name cannot be changed after profile creation.</InfoText>
          </FormGroup>
          
          <FormGroup>
            <label>Gender</label>
            <ReadOnlyField>{formData.gender || 'Not provided'}</ReadOnlyField>
            <InfoText>Gender cannot be changed after profile creation.</InfoText>
          </FormGroup>
          
          <FormGroup>
            <label>Date of Birth</label>
            <ReadOnlyField>{formatDateToLong(formData.birthDate) || 'Not provided'}</ReadOnlyField>
            <InfoText>Date of birth cannot be changed after profile creation.</InfoText>
          </FormGroup>
        </>
      ) : (
        <>
          <FormGroup hasError={!!errors.babyName}>
            <label htmlFor="babyName">Baby's First Name or Nickname <span style={{ color: 'var(--primary)' }}>*</span></label>
            <input
              type="text"
              className="form-control"
              id="babyName"
              name="babyName"
              value={formData.babyName || ''}
              onChange={handleChange}
              placeholder="Enter your baby's name"
              required
            />
            <div className="form-text">
              This cannot be changed after creating the profile.
            </div>
            {errors.babyName && <div className="error-message">{errors.babyName}</div>}
          </FormGroup>
          
          <FormGroup hasError={!!errors.gender}>
            <label htmlFor="gender">Gender <span style={{ color: 'var(--primary)' }}>*</span></label>
            <select
              className="form-select"
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            <div className="form-text">
              This cannot be changed after creating the profile.
            </div>
            {errors.gender && <div className="error-message">{errors.gender}</div>}
          </FormGroup>
          
          <FormGroup hasError={!!errors.birthDate}>
            <label htmlFor="birthDate">Baby's Date of Birth <span style={{ color: 'var(--primary)' }}>*</span></label>
            <input
              type="date"
              className="form-control"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              required
            />
            <div className="form-text">
              This cannot be changed after creating the profile.
            </div>
            {errors.birthDate && <div className="error-message">{errors.birthDate}</div>}
          </FormGroup>
        </>
      )}
      
      <FormGroup>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="isPremature"
            name="isPremature"
            checked={formData.isPremature}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="isPremature">
            Was your baby born premature?
          </label>
        </div>
      </FormGroup>
      
      {formData.isPremature && (
        <FormGroup hasError={!!errors.correctedAge}>
          <label htmlFor="correctedAge">Corrected Age</label>
          <input
            type="text"
            className="form-control"
            id="correctedAge"
            name="correctedAge"
            value={formData.correctedAge}
            onChange={handleChange}
            placeholder="e.g., 4 months"
          />
          <div className="form-text">
            This helps us provide age-appropriate recommendations
          </div>
          {errors.correctedAge && <div className="error-message">{errors.correctedAge}</div>}
        </FormGroup>
      )}
      
      <FormRow>
        <FormGroup hasError={!!errors.bedtime}>
          <label htmlFor="bedtime">Typical Bedtime </label>
          <input
            type="time"
            className="form-control"
            id="bedtime"
            name="bedtime"
            value={formData.bedtime || ''}
            onChange={(e) => {
              // Convert time to PM if needed
              const timeValue = e.target.value;
              if (timeValue) {
                const [hours, minutes] = timeValue.split(':');
                let hoursNum = parseInt(hours, 10);
                // If it's before noon (except 12 PM), add 12 hours to make it PM
                if (hoursNum < 12) {
                  hoursNum += 12;
                }
                const formattedTime = `${hoursNum.toString().padStart(2, '0')}:${minutes}`;
                const event = {
                  target: {
                    name: 'bedtime',
                    value: formattedTime,
                    type: 'time'
                  }
                };
                handleChange(event);
              } else {
                handleChange(e);
              }
            }}
            required
          />
          <div className="form-text">
            Bedtime is automatically set to PM
          </div>
          {errors.bedtime && <div className="error-message">{errors.bedtime}</div>}
        </FormGroup>
        
        <FormGroup hasError={!!errors.wakeupTime}>
          <label htmlFor="wakeupTime">Typical Wake-up Time </label>
          <input
            type="time"
            className="form-control"
            id="wakeupTime"
            name="wakeupTime"
            value={formData.wakeupTime || ''}
            onChange={(e) => {
              // Convert time to AM if needed
              const timeValue = e.target.value;
              if (timeValue) {
                const [hours, minutes] = timeValue.split(':');
                let hoursNum = parseInt(hours, 10);
                // If it's afternoon or evening, subtract 12 hours to make it AM
                if (hoursNum >= 12 && hoursNum !== 24) {
                  hoursNum -= 12;
                }
                const formattedTime = `${hoursNum.toString().padStart(2, '0')}:${minutes}`;
                const event = {
                  target: {
                    name: 'wakeupTime',
                    value: formattedTime,
                    type: 'time'
                  }
                };
                handleChange(event);
              } else {
                handleChange(e);
              }
            }}
            required
          />
          <div className="form-text">
            Wake-up time is automatically set to AM
          </div>
          {errors.wakeupTime && <div className="error-message">{errors.wakeupTime}</div>}
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup hasError={!!errors.numberOfNaps}>
          <label htmlFor="numberOfNaps">Number of Naps</label>
          <select
            className="form-select"
            id="numberOfNaps"
            name="numberOfNaps"
            value={formData.numberOfNaps || ''}
            onChange={handleChange}
            required
          >
            <option value="">Select option</option>
            <option value="0">No naps</option>
            <option value="1">1 nap per day</option>
            <option value="2">2 naps per day</option>
            <option value="3">3 naps per day</option>
            <option value="4+">4+ naps per day</option>
          </select>
          {errors.numberOfNaps && <div className="error-message">{errors.numberOfNaps}</div>}
        </FormGroup>
        
        {formData.numberOfNaps !== "0" && (
          <FormGroup hasError={!!errors.napDurations}>
            <label htmlFor="napDurations">Approximate Nap Durations</label>
            <select
              className="form-select"
              id="napDurations"
              name="napDurations"
              value={formData.napDurations || ''}
              onChange={handleChange}
              required={formData.numberOfNaps !== "0"}
            >
              <option value="">Select option</option>
              <option value="Short (less than 30 minutes)">Short (less than 30 minutes)</option>
              <option value="Medium (30-60 minutes)">Medium (30-60 minutes)</option>
              <option value="Long (more than 60 minutes)">Long (more than 60 minutes)</option>
              <option value="Varies">Varies</option>
            </select>
            {errors.napDurations && <div className="error-message">{errors.napDurations}</div>}
          </FormGroup>
        )}
      </FormRow>
      
      <FormGroup>
        <label>Sleep Associations (select all that apply)</label>
        <CheckboxGroup>
          <CheckboxLabel checked={formData.sleepAssociations.rocked}>
            <input
              type="checkbox"
              name="sleepAssociations.rocked"
              checked={formData.sleepAssociations.rocked}
              onChange={handleChange}
            />
            Rocked
          </CheckboxLabel>
          <CheckboxLabel checked={formData.sleepAssociations.dummy}>
            <input
              type="checkbox"
              name="sleepAssociations.dummy"
              checked={formData.sleepAssociations.dummy}
              onChange={handleChange}
            />
            Dummy/Pacifier
          </CheckboxLabel>
          <CheckboxLabel checked={formData.sleepAssociations.fedToSleep}>
            <input
              type="checkbox"
              name="sleepAssociations.fedToSleep"
              checked={formData.sleepAssociations.fedToSleep}
              onChange={handleChange}
            />
            Fed to sleep
          </CheckboxLabel>
          <CheckboxLabel checked={formData.sleepAssociations.independentlySleeps}>
            <input
              type="checkbox"
              name="sleepAssociations.independentlySleeps"
              checked={formData.sleepAssociations.independentlySleeps}
              onChange={handleChange}
            />
            Independently falls asleep
          </CheckboxLabel>
        </CheckboxGroup>
      </FormGroup>
      
      <FormGroup hasError={!!errors.selfSettles}>
        <label htmlFor="selfSettles">Does baby self-settle?</label>
        <select
          className="form-select"
          id="selfSettles"
          name="selfSettles"
          value={formData.selfSettles || ''}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="Sometimes">Sometimes</option>
        </select>
        {errors.selfSettles && <div className="error-message">{errors.selfSettles}</div>}
      </FormGroup>
    </FormSection>
  );
};

export default BasicInfoSection;
